import React, { useCallback, useMemo } from 'react';
import { Alert, Box, Button, Flex, HStack, Icon, Spinner, Text, VStack } from '@televet/kibble-ui';
import { openTrupanionPortal, ProcessStep } from './TrupanionCheckout';
import { TrupanionAvailability, useFindUniqueClinicPetParentQuery } from 'shared/types/graphql';
import { useTracking } from 'shared/providers/TrackingProvider';
import { TrupanionEvents } from 'shared/providers/TrackingProvider/EventNames';

type TrupanionAlertProps = {
  processStep: ProcessStep;
  eligiblePets: TrupanionAvailability[];
  isCertificateError: boolean;
  openModal: () => void;
  selectedPets: TrupanionAvailability[];
  certificateReferences: { [clinicPetId: string]: string };
  clinicPetParentId: string;
};

const TrupanionAlert = ({
  certificateReferences,
  selectedPets,
  openModal,
  processStep,
  eligiblePets,
  isCertificateError,
  clinicPetParentId,
}: TrupanionAlertProps): JSX.Element => {
  const { data: clinicPetParentData } = useFindUniqueClinicPetParentQuery({
    variables: { where: { id: clinicPetParentId } },
  });

  const { trackEvent } = useTracking();

  const onViewModalClick = useCallback(() => {
    trackEvent(TrupanionEvents.OfferViewClicked);
    openModal();
  }, [openModal, trackEvent]);

  const AlertDescription = useMemo(() => {
    return (
      <Flex pb={1} w="100%">
        {processStep === ProcessStep.PetSelection && eligiblePets.length > 0 && (
          <Flex w="100%" justify="flex-end">
            <Button size="xs" mt={2} onClick={onViewModalClick}>
              Get Started
            </Button>
          </Flex>
        )}

        {processStep === ProcessStep.PetSelection && eligiblePets.length === 0 && (
          <VStack align="flex-start">
            <Text variant="subtle">View details within the Trupanion Portal.</Text>
            <Button size="xs" onClick={openTrupanionPortal}>
              Open Trupanion Portal
            </Button>
          </VStack>
        )}

        {processStep === ProcessStep.Confirmation && (
          <VStack justify="flex-start" mt={3}>
            {Object.keys(certificateReferences).map((clinicPetId) => {
              return (
                <HStack spacing={0} key={clinicPetId}>
                  {certificateReferences[clinicPetId] ? (
                    <Icon variant="success" name="checkmarkCircle" />
                  ) : (
                    <Icon variant="danger" name="closeCircle" />
                  )}
                  <Text paddingLeft={1}>{`${
                    selectedPets.find((pet) => pet.clinicPetId === clinicPetId)?.clinicPetName
                  }`}</Text>
                </HStack>
              );
            })}
            {isCertificateError && (
              <Flex flex="1" mt={3} justifyContent="flex-end">
                <Button size="xs" onClick={openTrupanionPortal}>
                  Open Trupanion Portal
                </Button>
              </Flex>
            )}
          </VStack>
        )}
      </Flex>
    );
  }, [certificateReferences, eligiblePets.length, isCertificateError, onViewModalClick, processStep, selectedPets]);

  const alertTitle = useMemo(() => {
    if (processStep === ProcessStep.Initial) {
      return (
        <Flex>
          <Box>
            <Spinner size="md" />
          </Box>
          <Box ml={2}>
            <Text>Checking Trupanion Exam Day Offer eligibility...</Text>
          </Box>
        </Flex>
      );
    }
    if (processStep === ProcessStep.PetSelection && eligiblePets.length > 0) {
      return `Hooray! ${
        clinicPetParentData?.findUniqueClinicPetParent?.firstName || ''
      } has a pet that would benefit from a Trupanion Exam Day Offer.`;
    }
    if (processStep === ProcessStep.PetSelection && eligiblePets.length === 0) {
      return `${
        clinicPetParentData?.findUniqueClinicPetParent?.firstName || ''
      } may not have a pet that is qualified for a Trupanion Exam Day Offer.`;
    }
    if (processStep === ProcessStep.Loading) {
      return (
        <Flex>
          <Box>
            <Spinner size="md" />
          </Box>
          <Box ml={2}>
            <Text>Sending Trupanion Exam Day Offer{selectedPets.length > 1 ? 's' : ''}</Text>
          </Box>
        </Flex>
      );
    }
    if (processStep === ProcessStep.Confirmation && eligiblePets.length > 0) {
      return isCertificateError
        ? 'Oops! Some Trupanion Day Offers were unable to send.'
        : `Hooray! Trupanion Exam Day Offers have been sent for:`;
    }
  }, [
    clinicPetParentData?.findUniqueClinicPetParent?.firstName,
    eligiblePets.length,
    isCertificateError,
    processStep,
    selectedPets.length,
  ]);

  return (
    <Alert
      flexDirection="column"
      marginX={3}
      marginBottom={3}
      title={alertTitle}
      hideCloseButton
      hideIcon
      status="warning"
      description={AlertDescription}
    />
  );
};

export default TrupanionAlert;
